import { ref } from 'vue';
import { defineStore } from 'pinia';

export type ToastType = 'info' | 'success' | 'error' | 'warning';

export interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

export interface ConfirmDialog {
  show: boolean;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  confirmClass?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

export type DemoRole = 'landlord' | 'tenant' | 'accountant';

export interface StoreUser {
  id?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  primaryEmailAddress?: { emailAddress: string };
  publicMetadata?: Record<string, unknown>;
}

export const useAppStore = defineStore('app', () => {
  // State
  const isAuthenticated = ref(false);
  const user = ref<StoreUser | null>(null);
  const authLoading = ref(true);
  const error = ref<string | null>(null);
  const toasts = ref<Toast[]>([]);
  const confirmDialog = ref<ConfirmDialog>({ show: false });
  const isDemoMode = ref(false);
  const demoRole = ref<DemoRole | null>(null);

  // Actions
  function showToast(message: string, type: ToastType = 'info'): void {
    const id = Date.now();
    toasts.value.push({ id, message, type });
    setTimeout(() => {
      const index = toasts.value.findIndex((t) => t.id === id);
      if (index !== -1) toasts.value.splice(index, 1);
    }, 3000);
  }

  function showConfirm(title: string, message: string): Promise<boolean> {
    return new Promise((resolve) => {
      confirmDialog.value = {
        show: true,
        title,
        message,
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        confirmClass: 'bg-red-600',
        onConfirm: () => {
          confirmDialog.value.show = false;
          resolve(true);
        },
        onCancel: () => {
          confirmDialog.value.show = false;
          resolve(false);
        },
      };
    });
  }

  function $reset(): void {
    isAuthenticated.value = false;
    user.value = null;
    authLoading.value = true;
    error.value = null;
    toasts.value = [];
    confirmDialog.value = { show: false };
    isDemoMode.value = false;
    demoRole.value = null;
  }

  return {
    // State
    isAuthenticated,
    user,
    authLoading,
    error,
    toasts,
    confirmDialog,
    isDemoMode,
    demoRole,
    // Actions
    showToast,
    showConfirm,
    $reset,
  };
});
